import React from 'react';
import { Helmet } from 'react-helmet';
import { graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  greyColours,
  brandColours,
  fontSize,
} from '../styles';
import { Container, Link } from './ui';

const StyledBreadcrumbs = styled.div`
  position: relative;
`;

const StyledInner = styled.div`
  ${({ alt }) => {
    if (!alt) {
      return css`
        ${maxBreakpointQuery.medium`
          position: relative;
          margin-right: -20px;
          margin-left: -20px;
        `}

        &:before,
        &:after {
          ${maxBreakpointQuery.medium`
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            width: 30px;
          `}
        }

        &:before {
          ${maxBreakpointQuery.medium`
            left: 0;
            background: linear-gradient(
              to left,
              ${standardColours.transparent},
              ${({ altTheme }) =>
                altTheme === true
                  ? brandColours.denary
                  : altTheme === false
                  ? brandColours.senary
                  : standardColours.white}
            );
          `}
        }

        &:after {
          ${maxBreakpointQuery.medium`
            right: 0;
            background: linear-gradient(
              to right,
              ${standardColours.transparent},
              ${({ altTheme }) =>
                altTheme === true
                  ? brandColours.denary
                  : altTheme === false
                  ? brandColours.senary
                  : standardColours.white}
            );
          `}
        }
      `;
    }
  }}
`;

const StyledList = styled.ol`
  display: flex;
  align-items: center;
  gap: 6px;

  ${minBreakpointQuery.tiny`
    gap: 8px;
  `}

  ${({ alt }) => {
    if (alt) {
      return css`
        flex-wrap: wrap;
      `;
    } else {
      return css`
        padding: 16px 0;

        ${maxBreakpointQuery.medium`
          padding-right: 20px;
          padding-left: 20px;
          overflow: auto;
          white-space: nowrap;
        `}

        ${minBreakpointQuery.medium`
          flex-wrap: wrap;
        `}

        ${minBreakpointQuery.large`
          padding-top: 20px;
          padding-bottom: 20px;
        `}
      `;
    }
  }}

  ${({ inBannerWithLogo }) => {
    if (inBannerWithLogo) {
      return css`
        padding-right: 140px;

        ${minBreakpointQuery.small`
          padding-right: 150px;
        `}

        ${minBreakpointQuery.medium`
          padding-right: 160px;
        `}

        ${minBreakpointQuery.large`
          padding-right: 170px;
        `}
      `;
    }
  }}
`;

const StyledItem = styled.li`
  position: relative;
  display: flex;
  align-items: baseline;
  padding-left: 12px;
  ${fontSize(12)};

  ${minBreakpointQuery.tiny`
    padding-left: 16px;
    ${fontSize(14)};
  `}

  &:before {
    content: '';
    position: absolute;
    top: 5px;
    left: -1px;
    display: block;
    height: 6px;
    width: 6px;
    border: 2px solid
      ${({ alt, altTheme, isSpaListing }) =>
        alt || altTheme
          ? standardColours.white
          : isSpaListing
          ? greyColours.darkPurplishGrey
          : brandColours.primary};
    border-left: none;
    border-bottom: none;
    transform: rotate(45deg);

    ${minBreakpointQuery.tiny`
      height: 8px;
      width: 8px;
    `}
  }

  &:first-child {
    padding-left: 0;

    &:before {
      content: none;
    }
  }

  ${({ alt, isSpaListing }) => {
    if (alt) {
      return css`
        color: ${standardColours.white};
      `;
    } else if (isSpaListing) {
      return css`
        color: ${greyColours.darkPurplishGrey};
      `;
    }
  }}
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
`;

const Breadcrumbs = ({
  items,
  alt,
  altTheme,
  inBannerWithLogo,
  isSpaListing,
  ...props
}) =>
  items && (
    <>
      <Helmet
        script={[
          {
            type: 'application/ld+json',
            innerHTML: `{
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  ${items.map(({ text, url }, i) =>
                    JSON.stringify({
                      '@type': 'ListItem',
                      position: i + 1,
                      name: text,
                      ...(url && {
                        item:
                          url === '/'
                            ? process.env.GATSBY_SITE_URL
                            : process.env.GATSBY_SITE_URL + url,
                      }),
                    })
                  )}
                ]
              }`,
          },
        ]}
      />
      <StyledBreadcrumbs {...props}>
        <Container>
          <StyledInner alt={alt} altTheme={altTheme}>
            <StyledList alt={alt} inBannerWithLogo={inBannerWithLogo}>
              {items.map(({ text, url }, i) => (
                <StyledItem
                  key={`breadcrumb-${i}`}
                  alt={alt}
                  altTheme={altTheme}
                  isSpaListing={isSpaListing}
                >
                  {url ? <StyledLink to={url}>{text}</StyledLink> : text}
                </StyledItem>
              ))}
            </StyledList>
          </StyledInner>
        </Container>
      </StyledBreadcrumbs>
    </>
  );

export default Breadcrumbs;

export const BreadcrumbsFragment = graphql`
  fragment BreadcrumbsFragment on SPASEEKERS_Breadcrumb {
    text
    url
  }
`;
